import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, FormControl, InputLabel, Select, MenuItem,
  Alert, Box, Typography,
} from '@mui/material';
import { apiService } from '../services/apiService';

const CreateMotdDialog = ({ open, onClose, observatoryId, observatoryName, onCreated }) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('info');
  const [deliveryType, setDeliveryType] = useState('message');
  const [expiresAt, setExpiresAt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!title.trim() || !message.trim()) {
      setError('Title and message are required');
      return;
    }

    setLoading(true);
    setError(null);

    const effectiveExpiry = expiresAt || getDefaultExpiry();

    try {
      const result = await apiService.createMotd(observatoryId, {
        title: title.trim(),
        message: message.trim(),
        severity,
        delivery_type: deliveryType,
        expires_at: new Date(effectiveExpiry).toISOString(),
      });

      if (result.success) {
        setTitle('');
        setMessage('');
        setSeverity('info');
        setDeliveryType('message');
        setExpiresAt('');
        onCreated?.(result.data);
        // Notify Admin Control Center listeners (AnnouncementsCard,
        // AnnouncementsPane) so the new observatory MOTD shows up in
        // the count + table without a manual refresh.
        try {
          window.postMessage({ type: 'motdChanged' }, '*');
          if (window.parent !== window) {
            window.parent.postMessage({ type: 'motdChanged' }, '*');
          }
        } catch (_) { /* best-effort */ }
        onClose();
      } else {
        setError(result.error || 'Failed to create MOTD');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create MOTD');
    } finally {
      setLoading(false);
    }
  };

  // Default expiration: 7 days from now
  const getDefaultExpiry = () => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 16);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{observatoryName ? `${observatoryName} — New Message of the Day` : 'New Message of the Day'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            inputProps={{ maxLength: 200 }}
          />

          <TextField
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            required
            multiline
            rows={4}
            inputProps={{ maxLength: 5000 }}
          />

          <FormControl fullWidth>
            <InputLabel>Severity</InputLabel>
            <Select value={severity} onChange={(e) => setSeverity(e.target.value)} label="Severity">
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Delivery</InputLabel>
            <Select value={deliveryType} onChange={(e) => setDeliveryType(e.target.value)} label="Delivery">
              <MenuItem value="message">Message only</MenuItem>
              <MenuItem value="email">Email only</MenuItem>
              <MenuItem value="both">Message + Email</MenuItem>
            </Select>
          </FormControl>

          {(deliveryType === 'email' || deliveryType === 'both') && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Email will be sent to all observatory members. Recipients will be auto-acknowledged.
            </Alert>
          )}

          <TextField
            label="Expires At"
            type="datetime-local"
            value={expiresAt || getDefaultExpiry()}
            onChange={(e) => setExpiresAt(e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />

          <Typography variant="caption" color="text.secondary">
            MOTD will be displayed to all observatory members until acknowledged or expired.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !title.trim() || !message.trim()}
        >
          {loading ? 'Creating...' : deliveryType === 'message' ? 'Create' : 'Create & Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateMotdDialog;
